import { useState } from 'react';
import { X, CreditCard, AlertTriangle } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';

interface RecordPaymentModalProps {
    purchase: {
        id: string;
        memberId: string;
        invoiceId: string | null;
        totalAmount: string | number | null;
        paidAmount: string | number | null;
        pendingAmount: string | number | null;
        paymentStatus: string;
        plan: { name: string };
        member: { fullName: string | null; displayName: string | null };
    };
    onClose: () => void;
    onSuccess: () => void;
}

export default function RecordPaymentModal({ purchase, onClose, onSuccess }: RecordPaymentModalProps) {
    const pendingAmt = Number(purchase.pendingAmount || 0);
    const totalAmt = Number(purchase.totalAmount || 0);
    const paidAmt = Number(purchase.paidAmount || 0);

    const [amount, setAmount] = useState<string>(pendingAmt.toFixed(2));
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [discountAmount, setDiscountAmount] = useState('0');
    const [discountPercent, setDiscountPercent] = useState('0');
    const [taxPercent, setTaxPercent] = useState('0');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    const amountNum = Number(amount) || 0;
    const taxAmt = Number(((amountNum * Number(taxPercent)) / 100).toFixed(2));

    const handleSubmit = async () => {
        if (amountNum <= 0) {
            toast.error('Amount must be greater than 0');
            return;
        }
        if (amountNum > pendingAmt) {
            toast.error(`Amount cannot exceed pending amount of ₹${pendingAmt.toFixed(2)}`);
            return;
        }
        if (!paymentMethod) {
            toast.error('Please select a payment method');
            return;
        }

        setLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) throw new Error('No session');

            const response = await fetch(
                `${API_BASE_URL}/api/members/${purchase.memberId}/memberships/${purchase.id}/record-payment`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        amount: amountNum,
                        paymentMethod,
                        paymentDate,
                        discountAmount: Number(discountAmount),
                        discountPercent: Number(discountPercent),
                        taxPercent: Number(taxPercent),
                        notes: notes || undefined
                    })
                }
            );

            const data = await response.json();
            if (response.ok) {
                toast.success(data.message || 'Payment recorded successfully');
                onSuccess();
            } else {
                toast.error(data.error || 'Failed to record payment');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to record payment');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-base font-black text-gray-900 tracking-tight">Record Payment</h2>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{purchase.plan.name}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Payment summary */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gray-50 rounded-2xl p-3 text-center">
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Total</p>
                            <p className="text-sm font-black text-gray-900 mt-1">₹{totalAmt.toLocaleString()}</p>
                        </div>
                        <div className="bg-green-50 rounded-2xl p-3 text-center">
                            <p className="text-[9px] font-black text-green-600 uppercase tracking-widest">Paid</p>
                            <p className="text-sm font-black text-green-700 mt-1">₹{paidAmt.toLocaleString()}</p>
                        </div>
                        <div className="bg-amber-50 rounded-2xl p-3 text-center">
                            <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Pending</p>
                            <p className="text-sm font-black text-amber-700 mt-1">₹{pendingAmt.toFixed(2)}</p>
                        </div>
                    </div>

                    {/* Amount */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount (₹)</label>
                        <input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            min="0.01"
                            max={pendingAmt}
                            step="0.01"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-lg focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                        />
                        {amountNum > 0 && amountNum < pendingAmt && (
                            <div className="flex items-center gap-2 text-amber-600">
                                <AlertTriangle className="w-3 h-3" />
                                <span className="text-[10px] font-bold">Still pending: ₹{(pendingAmt - amountNum).toFixed(2)}</span>
                            </div>
                        )}
                    </div>

                    {/* Payment Method & Date */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Method</label>
                            <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                            >
                                <option value="">Select</option>
                                <option value="UPI">UPI</option>
                                <option value="Cash">Cash</option>
                                <option value="Card">Card</option>
                                <option value="Transfer">Transfer</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</label>
                            <input
                                type="date"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                                className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                            />
                        </div>
                    </div>

                    {/* Discount & GST */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Discount ₹</label>
                            <input
                                type="number"
                                value={discountAmount}
                                onChange={(e) => setDiscountAmount(e.target.value)}
                                min="0"
                                step="0.01"
                                className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Discount %</label>
                            <input
                                type="number"
                                value={discountPercent}
                                onChange={(e) => setDiscountPercent(e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">GST %</label>
                            <input
                                type="number"
                                value={taxPercent}
                                onChange={(e) => setTaxPercent(e.target.value)}
                                min="0"
                                max="100"
                                step="0.01"
                                className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm"
                            />
                        </div>
                    </div>
                    {taxAmt > 0 && (
                        <p className="text-[10px] text-gray-400 font-bold -mt-3">GST on this payment: ₹{taxAmt}</p>
                    )}

                    {/* Notes */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder="Optional..."
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm resize-none"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 bg-white border border-gray-200 text-gray-600 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-50 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading || !paymentMethod || amountNum <= 0 || amountNum > pendingAmt}
                        className="flex-[2] py-3 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {loading && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                        {loading ? 'Recording...' : `Record ₹${amountNum.toLocaleString()}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
